/**
 * La scheda di un preventivo sul computer, ridisegnata il 26/10/2026 come quella della
 * fattura (stessa famiglia di pagina: features/invoices/fattura.css, classi .fd-).
 *
 * La domanda: il cliente l'ha visto, l'ha firmato? E cosa faccio adesso?
 * La forma: la risposta in una frase con le date vere (mandato, aperto, firmato), una sola
 * azione principale, la proposta come la legge il cliente; accanto il cliente, il percorso
 * del preventivo passo per passo con le sue date, il link per firmare e le altre azioni
 * con una riga che dice cosa fanno.
 *
 * Prima: titolo gigante, il filo dei soldi e il ciclo a cinque pallini uno sopra l'altro,
 * il tracciamento in fondo a destra e le azioni in un menu ⋯ senza spiegazione.
 * Sul telefono la scheda è PreventivoMobile.
 */
import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { openModal } from '../../stores/modal.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { Helper } from '../../components/ui/Helper';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { fmtEur } from '../../lib/utils';
import { adminLinks } from '../../lib/adminRoutes';
import { giorniDaOggi, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, nomeBreve } from '../admin/fatture/fattureApi';
import { useIsMobile } from '../../hooks/useIsMobile';
import { PreventivoMobile } from './PreventivoMobile';
import '../invoices/fattura.css';

interface QuoteFull {
  id: string;
  number: string | null;
  title: string | null;
  introMessage: string | null;
  termsAndConditions: string | null;
  notes: string | null;
  status: string;
  issueDate: string | null;
  validUntil: string | null;
  subtotal: string; vat: string; total: string;
  sentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  signerName: string | null;
  signerEmail: string | null;
  signerIp: string | null;
  signatureDataUrl: string | null;
  documentHash: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  convertedToInvoiceId: string | null;
  convertedAt: string | null;
  convertedToInvoice: { id: string; number: string | null; status: string } | null;
  signatureToken: string | null;
  client: { id: string; legalName: string; vat: string | null; sdiCode: string | null; pec: string | null };
  project: { id: string; code: string; name: string } | null;
  lines: Array<{ id: string; description: string; quantity: string; unitPrice: string; vatRate: string; order: number }>;
}

interface ClientContact { id: string; fullName: string; email: string | null; isPrimary: boolean; }

const euro = (n: number | string) => `€ ${fmtEur(Number(n), true)}`;

/**
 * Sul telefono la scheda è PreventivoMobile (in cima quanto vale e a che punto
 * è la firma, il gesto che serve in fondo). Sul computer resta questa.
 */
export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();
  if (isMobile && id) return <div className="page"><PreventivoMobile id={id} /></div>;
  return <QuoteDetailDesktop />;
}

function QuoteDetailDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sendModalOpen, setSendModalOpen] = useState(false);

  const { data: q, isLoading, isError } = useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.get<QuoteFull>(`/quotes/${id}`),
    enabled: !!id,
    retry: false,
  });

  const sendQuote = useMutation({
    mutationFn: (payload: { to: string; cc?: string; subject: string; message: string }) =>
      api.post(`/quotes/${id}/send`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quote', id] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
      setSendModalOpen(false);
      toast.ok('Preventivo mandato al cliente', 'Riceverà una mail con il link per firmare.');
    },
    onError: (e: any) => toast.alert('Preventivo non mandato', e?.message ?? 'Riprova'),
  });

  const convert = useMutation({
    mutationFn: () => api.post<{ invoiceId: string }>(`/quotes/${id}/convert-to-invoice`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['quote', id] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.ok('Fattura creata dal preventivo', 'Apro la bozza della fattura…');
      navigate(`/invoices/${res.invoiceId}`);
    },
    onError: (e: any) => toast.alert('Fattura non creata dal preventivo', e?.message ?? 'Riprova'),
  });

  const duplicate = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/quotes/${id}/duplicate`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.ok('Preventivo copiato', 'Apro la copia in bozza…');
      navigate(`/quotes/${res.id}`);
    },
    onError: (e: any) => toast.alert('Preventivo non copiato', e?.message ?? 'Riprova'),
  });

  // Rifiuto reversibile: niente conferma, si annulla dal toast (come Gmail).
  const reject = useMutation({
    mutationFn: (vars: { reason: string; prevStatus: string }) =>
      api.post(`/quotes/${id}/reject`, { reason: vars.reason }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['quote', id] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.undo('Preventivo segnato rifiutato', {
        onUndo: () =>
          api.patch(`/quotes/${id}`, { status: vars.prevStatus }).then(() => {
            qc.invalidateQueries({ queryKey: ['quote', id] });
            qc.invalidateQueries({ queryKey: ['quotes'] });
          }),
      });
    },
    onError: (e: any) => toast.alert('Preventivo non segnato rifiutato', e?.message ?? 'Riprova'),
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/quotes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.ok('Preventivo eliminato');
      navigate(adminLinks.preventivi());
    },
    onError: (e: any) => toast.alert('Preventivo non eliminato', e?.message ?? 'Riprova'),
  });

  if (!id) return <Navigate to={adminLinks.preventivi()} replace />;
  if (isError) return (
    <div className="page quote-detail-page">
      <Breadcrumb items={[{ label: 'Preventivi', to: adminLinks.preventivi() }, { label: 'Preventivo' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Preventivo non trovato</h1>
        <div className="mute mt-2">Questo preventivo non esiste o è stato eliminato.</div>
      </div>
    </div>
  );
  if (isLoading || !q) return <DetailSkeleton header="chips" />;

  const isDraft = q.status === 'BOZZA';
  const isSent = q.status === 'INVIATO' || q.status === 'VISUALIZZATO';
  const isSigned = q.status === 'ACCETTATO';
  const isConverted = q.status === 'CONVERTITO';
  const isRejected = q.status === 'RIFIUTATO' || q.status === 'SCADUTO';
  const chi = nomeBreve(q.client.legalName);
  const signLink = q.signatureToken ? `${window.location.origin}/sign/quotes/${q.signatureToken}` : null;
  const titolo = q.title || `Proposta per ${chi}`;
  const occupato = sendQuote.isPending || convert.isPending || duplicate.isPending || reject.isPending || del.isPending;

  const creaProgetto = () => openModal('newProject', { clientId: q.client.id, quoteId: id, title: titolo, value: Number(q.total) || 0 });

  // ──── L'azione che serve adesso: una sola.
  let adesso: { key: string; titolo: string; spiega: string; cta: string; icon: IconName; onClick: () => void; pending?: boolean } | null = null;
  if (isDraft) adesso = { key: 'send', titolo: 'Mandalo al cliente', spiega: 'Riceve una mail con il link per leggerlo e firmarlo online, gratis. La firma vale come un contratto.', cta: 'Manda al cliente', icon: 'send', onClick: () => setSendModalOpen(true) };
  else if (isSent) adesso = { key: 'resend', titolo: q.status === 'VISUALIZZATO' ? "L'ha aperto ma non l'ha ancora firmato" : 'Aspettiamo che lo apra e lo firmi', spiega: 'Se tarda, rimandagli il link o sentilo: il link resta lo stesso.', cta: 'Rimanda il link', icon: 'mail', onClick: () => setSendModalOpen(true) };
  else if (isSigned && !q.project) adesso = { key: 'project', titolo: 'Firmato: apri il progetto', spiega: 'Cliente, titolo e valore arrivano già dal preventivo. Poi da qui si fa anche la fattura.', cta: 'Crea il progetto', icon: 'cube', onClick: creaProgetto };
  else if (isSigned) adesso = { key: 'convert', titolo: 'Firmato: fai la fattura', spiega: 'Si crea la bozza della fattura con cliente e righe già compilati: la controlli e la emetti.', cta: 'Crea la fattura', icon: 'invoice', onClick: () => convert.mutate(), pending: convert.isPending };
  else if (isConverted && q.convertedToInvoice) adesso = { key: 'open-invoice', titolo: `È diventato la fattura ${q.convertedToInvoice.number ?? ''}`, spiega: 'Incasso, rate e invio al cliente si seguono dalla fattura.', cta: 'Apri la fattura', icon: 'arrowRight', onClick: () => navigate(`/invoices/${q.convertedToInvoice!.id}`) };

  // Le azioni rare: nel menu ⋯ della testata (26/10/2026, "alleggerire"). Il link per
  // firmare si copia da lì, come sul telefono: prima era una scheda a sé.
  const altre: ActionItem[] = [
    { key: 'convert', icon: 'invoice', label: 'Crea la fattura da questo preventivo', onClick: () => convert.mutate(), hidden: !isSigned },
    { key: 'project', icon: 'cube', label: 'Crea il progetto da questo preventivo', onClick: creaProgetto, hidden: !isSigned || !!q.project },
    { key: 'resend', icon: 'mail', label: 'Rimanda il link al cliente', onClick: () => setSendModalOpen(true), hidden: !isSent },
    {
      key: 'copy-link', icon: 'copy', label: 'Copia il link per firmare', hidden: !signLink || isDraft || isConverted,
      onClick: () => { navigator.clipboard.writeText(signLink!); toast.ok('Link per firmare copiato', 'Incollalo dove vuoi: WhatsApp, mail, messaggio.'); },
    },
    { key: 'duplicate', icon: 'file', label: 'Fanne una copia in bozza', onClick: () => duplicate.mutate() },
    { key: 'reject', icon: 'x', label: 'Segnalo rifiutato dal cliente', onClick: () => reject.mutate({ reason: 'Rifiutato internamente', prevStatus: q.status }), hidden: isSigned || isConverted || isRejected },
    { key: 'print', icon: 'export', label: 'Stampa il preventivo o salvalo in PDF', onClick: () => window.print() },
    {
      key: 'delete', icon: 'trash', label: 'Elimina il preventivo', danger: true, hidden: isConverted,
      onClick: async () => {
        if (await confirmDialog({ title: 'Eliminare definitivamente questo preventivo?', body: 'Sparisce anche il link per firmare: se il cliente lo apre, non trova più niente.', danger: true, confirmLabel: 'Elimina' })) del.mutate();
      },
    },
  ];

  // Il percorso del preventivo, con le date vere. La frase solo sul passo di adesso.
  const passi: Array<{ key: string; titolo: string; quando: string | null; spiega: string }> = [
    { key: 'bozza', titolo: 'Bozza', quando: q.issueDate, spiega: 'Lo prepariamo noi: il cliente non lo vede.' },
    { key: 'mandato', titolo: 'Mandato al cliente', quando: q.sentAt, spiega: 'Ha ricevuto la mail con il link per firmare.' },
    { key: 'aperto', titolo: 'Aperto dal cliente', quando: q.viewedAt, spiega: "Ha aperto il link: l'ha letto ma non l'ha firmato." },
    { key: 'firmato', titolo: 'Firmato', quando: q.signedAt, spiega: 'Ha accettato: vale come un contratto.' },
    { key: 'fattura', titolo: 'Diventato fattura', quando: q.convertedAt, spiega: 'Da qui si segue la fattura.' },
  ];
  const passoAttuale = isConverted ? 4 : isSigned ? 3 : q.status === 'VISUALIZZATO' ? 2 : q.status === 'INVIATO' ? 1 : 0;

  return (
    <div className="page fd-page">
      <Breadcrumb items={[{ label: 'Preventivi', to: adminLinks.preventivi() }, { label: q.number ?? 'Preventivo' }]} />

      {/* ── 1. La risposta ─────────────────────────────────────────────── */}
      <header className="fd-hd">
        <SectionMark icon="file" className="fd-sez" />
        <div className="min-w-0">
          <div className="fd-kicker">
            Preventivo{q.number ? ` ${q.number}` : ''}{q.validUntil ? `, valido fino ${alGiorno(q.validUntil)}` : ''}
          </div>
          <h1 className="fd-title">{titolo}</h1>
          <p className="fd-risposta">{risposta(q, chi)}</p>
        </div>
        <div className="fd-hd-azioni">
          <ActionsMenu variant="ghost" label="Altre azioni sul preventivo" items={altre} excludeKey={adesso?.key} disabled={occupato} />
        </div>
      </header>

      <div className="fd-cols">
        <div className="fd-main">
          {/* ── 2. Cosa fare adesso ──────────────────────────────────────── */}
          {isRejected ? (
            <section className="fd-adesso is-grave">
              <span className="fd-adesso-ico"><Icon name="x" size={18} /></span>
              <span className="min-w-0">
                <span className="fd-adesso-t">{q.status === 'SCADUTO' ? 'Scaduto senza firma' : 'Rifiutato dal cliente'}</span>
                <span className="fd-adesso-s">{q.rejectionReason ? `Motivo: ${q.rejectionReason}. ` : ''}Se si riparte, fanne una copia in bozza e mandala di nuovo.</span>
              </span>
              <button className="btn" disabled={occupato} onClick={() => duplicate.mutate()}><Icon name="copy" size={12} /> Fanne una copia</button>
            </section>
          ) : adesso && (
            <section className="fd-adesso" aria-label="Cosa fare adesso">
              <span className="fd-adesso-ico"><Icon name={adesso.icon} size={18} /></span>
              <span className="min-w-0">
                <span className="fd-adesso-t">{adesso.titolo}</span>
                <span className="fd-adesso-s">{adesso.spiega}</span>
              </span>
              <button className="btn btn-primary" disabled={adesso.pending || occupato} onClick={adesso.onClick}>
                <Icon name={adesso.icon} size={12} /> {adesso.pending ? 'Attendi…' : adesso.cta}
              </button>
            </section>
          )}

          {/* ── 3. La proposta, come la legge il cliente ─────────────────── */}
          {q.introMessage && (
            <section className="fd-blocco">
              <h2 className="fd-h2"><Icon name="message" size={15} /> Il messaggio al cliente</h2>
              <p className="fd-nota">{q.introMessage}</p>
            </section>
          )}

          <section className="fd-blocco">
            <h2 className="fd-h2"><Icon name="list" size={15} /> Cosa c'è nella proposta</h2>
            {q.lines.length > 0 ? (
              <div className="fd-righe">
                <div className="fd-riga fd-riga--hd" aria-hidden>
                  <span>Descrizione</span><span>Quantità per prezzo</span><span>IVA</span><span>Con IVA</span>
                </div>
                {q.lines.map((l) => {
                  const qty = Number(l.quantity) || 0;
                  const price = Number(l.unitPrice) || 0;
                  const vatRate = Number(l.vatRate) || 0;
                  return (
                    <div key={l.id} className="fd-riga">
                      <span className="fd-riga-desc">{l.description}</span>
                      <span className="mute">{qty} × {euro(price)}</span>
                      <span className="mute">{vatRate}%</span>
                      <span className="fd-riga-tot">{euro(qty * price * (1 + vatRate / 100))}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="fd-vuoto">La proposta non ha ancora voci.</p>
            )}
            <dl className="fd-totali">
              <div><dt>Imponibile</dt><dd>{euro(q.subtotal)}</dd></div>
              <div><dt>IVA</dt><dd>{euro(q.vat)}</dd></div>
              <div className="is-tot"><dt>Totale con IVA</dt><dd>{euro(q.total)}</dd></div>
            </dl>
          </section>

          {q.termsAndConditions && (
            <section className="fd-blocco">
              <h2 className="fd-h2"><Icon name="file" size={15} /> Termini e condizioni</h2>
              <p className="fd-nota fd-nota--mute">{q.termsAndConditions}</p>
            </section>
          )}

          {q.notes && (
            <section className="fd-blocco">
              <h2 className="fd-h2"><Icon name="file" size={15} /> Nota interna <span className="fd-h2-sub">il cliente non la vede</span></h2>
              <p className="fd-nota">{q.notes}</p>
            </section>
          )}
        </div>

        <aside className="fd-side">
          {/* ── Il cliente ─────────────────────────────────────────────── */}
          <section className="fd-scheda">
            <Link to={`/clients/${q.client.id}`} className="fd-cliente">
              <BrandLogo name={q.client.legalName} size={40} />
              <span className="min-w-0">
                <span className="fd-cliente-t">{q.client.legalName}</span>
                <span className="fd-cliente-s">Apri la scheda del cliente</span>
              </span>
              <Icon name="chevronR" size={12} className="mute" />
            </Link>
            {q.client.vat && <dl className="fd-dati"><div><dt>Partita IVA</dt><dd className="mono">{q.client.vat}</dd></div></dl>}
          </section>

          {(q.project || q.convertedToInvoice) && (
            <section className="fd-scheda">
              <h2 className="fd-h3">Collegato a</h2>
              <div className="fd-link-lista">
                {q.project && (
                  <Link to={`/projects/${q.project.id}`} className="fd-link">
                    <Icon name="cube" size={14} /> <span className="min-w-0"><span className="t">{q.project.name}</span><span className="s">progetto {q.project.code}</span></span>
                  </Link>
                )}
                {q.convertedToInvoice && (
                  <Link to={`/invoices/${q.convertedToInvoice.id}`} className="fd-link">
                    <Icon name="invoice" size={14} /> <span className="min-w-0"><span className="t">La fattura {q.convertedToInvoice.number ?? 'in bozza'}</span><span className="s">nata da questo preventivo</span></span>
                  </Link>
                )}
              </div>
            </section>
          )}

          {/* ── Il percorso del preventivo: i passi fatti con la loro data ── */}
          {!isRejected && (
            <section className="fd-scheda">
              <h2 className="fd-h3">Il percorso del preventivo</h2>
              <ol className="fd-passi">
                {passi.map((s, i) => {
                  const stato = i < passoAttuale ? 'fatto' : i === passoAttuale ? 'ora' : 'poi';
                  return (
                    <li key={s.key} className={`fd-passo is-${stato}`}>
                      <span className="fd-passo-dot" aria-hidden>{stato !== 'poi' ? <Icon name="check" size={11} /> : null}</span>
                      <span className="min-w-0">
                        <span className="fd-passo-t">
                          {s.titolo}
                          {stato !== 'poi' && s.quando && <span className="fd-passo-data">{giornoInParole(s.quando)}</span>}
                          {stato === 'ora' && <span className="fd-passo-qui">è qui</span>}
                        </span>
                        {stato === 'ora' && <span className="fd-passo-s">{s.spiega}</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {/* ── La firma ────────────────────────────────────────────────── */}
          {(isSigned || isConverted) && q.signatureDataUrl && (
            <section className="fd-scheda">
              <h2 className="fd-h3">
                La firma del cliente{' '}
                <Helper text="Firma elettronica semplice (eIDAS): vale per i contratti commerciali nell'Unione europea. Come prova teniamo l'immagine della firma, l'indirizzo IP, l'ora e l'impronta (hash) del documento." />
              </h2>
              <div className="fd-firma"><img src={q.signatureDataUrl} alt="La firma del cliente" /></div>
              <dl className="fd-dati">
                <div><dt>Firmato da</dt><dd>{q.signerName ?? '—'}{q.signerEmail ? `, ${q.signerEmail}` : ''}</dd></div>
                {q.signedAt && <div><dt>Quando</dt><dd>{new Date(q.signedAt).toLocaleString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</dd></div>}
                {q.signerIp && <div><dt>Indirizzo IP</dt><dd className="mono">{q.signerIp}</dd></div>}
                {q.documentHash && <div><dt>Impronta del documento</dt><dd className="mono" title={q.documentHash}>{q.documentHash.slice(0, 20)}…{q.documentHash.slice(-8)}</dd></div>}
              </dl>
            </section>
          )}

        </aside>
      </div>

      {sendModalOpen && (
        <SendQuoteModal
          quote={q}
          onClose={() => setSendModalOpen(false)}
          onSend={(p) => sendQuote.mutate(p)}
          sending={sendQuote.isPending}
        />
      )}
    </div>
  );
}

/** La risposta in una frase: a che punto è la firma, con le date vere. Il rosso solo se è scaduto. */
function risposta(q: QuoteFull, chi: string): ReactNode {
  const g = giorniDaOggi(q.validUntil);
  const validita: ReactNode = q.validUntil
    ? g != null && g < 0 ? <> La proposta <span className="is-alert">è scaduta {ilGiorno(q.validUntil)}</span>.</> : <> Vale fino {alGiorno(q.validUntil)}.</>
    : null;
  switch (q.status) {
    case 'BOZZA': return <>È una <strong>bozza</strong>: {chi} non l'ha ancora ricevuto.</>;
    case 'INVIATO': return <>Mandato a {chi}{q.sentAt ? ` ${ilGiorno(q.sentAt)}` : ''}: <strong>non l'ha ancora aperto</strong>.{validita}</>;
    case 'VISUALIZZATO': return <>{chi} l'ha aperto{q.viewedAt ? ` ${ilGiorno(q.viewedAt)}` : ''} ma <strong>non l'ha ancora firmato</strong>.{validita}</>;
    case 'ACCETTATO': return <>{chi} l'ha <strong>firmato</strong>{q.signedAt ? ` ${ilGiorno(q.signedAt)}` : ''}{q.project ? ': ora si fa la fattura.' : ': ora si apre il progetto e si fa la fattura.'}</>;
    case 'CONVERTITO': return <>Firmato e <strong>diventato fattura</strong>{q.convertedAt ? ` ${ilGiorno(q.convertedAt)}` : ''}.</>;
    case 'RIFIUTATO': return <>{chi} l'ha <strong>rifiutato</strong>{q.rejectedAt ? ` ${ilGiorno(q.rejectedAt)}` : ''}.</>;
    case 'SCADUTO': return <>La proposta <span className="is-alert">è scaduta{q.validUntil ? ` ${ilGiorno(q.validUntil)}` : ''}</span> senza la firma di {chi}.</>;
    default: return null;
  }
}

// ════════════════════════════════════════════════════════════════════
function SendQuoteModal({ quote, onClose, onSend, sending }: {
  quote: QuoteFull;
  onClose: () => void;
  onSend: (p: { to: string; cc?: string; subject: string; message: string }) => void;
  sending: boolean;
}) {
  // I contatti del cliente, per proporre la mail giusta.
  const { data: contacts } = useQuery({
    queryKey: ['client-contacts', quote.client.id],
    queryFn: () => api.get<ClientContact[]>(`/clients/${quote.client.id}/contacts`).catch(() => []),
  });
  const primary = contacts?.find((c) => c.isPrimary) ?? contacts?.[0];

  const [to, setTo] = useState(primary?.email ?? '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(quote.title ?? `Preventivo ${quote.number ?? ''}`.trim());
  const [message, setMessage] = useState(`Ciao,\n\ncome promesso ti mando il preventivo. Trovi tutto nel link qui sotto: puoi visualizzarlo e firmarlo direttamente online, in pochi secondi.\n\nFammi sapere se hai dubbi o serve qualche aggiustamento.\n\nUn saluto`);

  // Quando i contatti arrivano dopo il primo disegno, si riempie il destinatario.
  if (!to && primary?.email) setTo(primary.email);

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 quote-email-overlay" onClick={onClose}>
      <div className="bg-white rounded-[var(--r-card)] border border-line shadow-lg w-[520px] max-w-[92vw] quote-email-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Manda il preventivo al cliente">
        <div className="p-4 border-b border-line flex items-center justify-between quote-email-hd">
          <div>
            <div className="t-micro">Il cliente lo firma online, gratis</div>
            <div className="t-h3 mt-0.5">Manda il preventivo al cliente</div>
          </div>
          <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
        </div>
        <div className="p-4 space-y-3 text-[13px] quote-email-bd">
          <div>
            <label className="field-label">
              A chi lo mandi
              <Helper text="La mail a cui arriva il link per firmare. Di solito il contatto principale del cliente: cambiala se lo deve firmare un'altra persona." />
            </label>
            <input className="inp" placeholder="cliente@esempio.it" value={to} onChange={(e) => setTo(e.target.value)} />
            {contacts && contacts.length > 1 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {contacts.filter((c) => c.email).map((c) => (
                  <button key={c.id} className="btn btn-sm btn-dashed" onClick={() => setTo(c.email!)}>
                    {c.fullName}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="field-label">In copia</label>
            <input className="inp" placeholder="Facoltativo" value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Oggetto</label>
            <input className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="field-label">
              Messaggio
              <Helper text="È la prima cosa che il cliente legge nella mail. Il link per firmare si aggiunge da solo, sotto." />
            </label>
            <textarea className="inp" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
        <div className="p-4 border-t border-line flex gap-2 quote-email-ft">
          <button className="btn" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button
            className="btn btn-primary"
            disabled={!to.trim() || !subject.trim() || sending}
            onClick={() => onSend({ to: to.trim(), cc: cc.trim() || undefined, subject: subject.trim(), message: message.trim() })}
          >
            <Icon name="send" size={12} /> {sending ? 'Invio in corso…' : 'Manda il preventivo'}
          </button>
        </div>
      </div>
    </div>
  );
}
